import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { ChevronLeft, Calendar, Clock, Building2, MapPin, FileText, Paperclip, AlignLeft, Check, RotateCcw, Users } from 'lucide-react';
import { todayStr, toDateStr } from '../utils/dateUtils';

const makeEmptyForm = (tanggal) => ({
  tanggal: tanggal || todayStr(),
  jam_mulai: '', jam_selesai: '',
  nama_acara: '', penyelenggara: '', lokasi: '',
  no_surat: '', keterangan: '',
});

export default function InputJadwalPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');
  const defaultTanggal = searchParams.get('tanggal');
  const isEdit = !!editId;

  const [form, setForm] = useState(makeEmptyForm(defaultTanggal));
  const [file, setFile] = useState(null);
  const [existingFile, setExistingFile] = useState(null);
  const [pimpinanList, setPimpinanList] = useState([]);
  const [selectedPimpinan, setSelectedPimpinan] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [selectDisposisi, setSelectDisposisi] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    document.title = "Input Jadwal - Penjadwalan Acara Pimpinan";
    api.get('/pimpinan?active=true')
      .then(res => setPimpinanList(res.data.data))
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    setLoadingEdit(true);
    api.get(`/jadwal/${editId}`)
      .then(res => {
        const j = res.data.data;
        setForm({
          // Fix: toDateStr() agar tidak kena UTC shift
          tanggal: toDateStr(j.tanggal),
          jam_mulai: j.jam_mulai?.slice(0, 5) || '',
          jam_selesai: j.jam_selesai?.slice(0, 5) || '',
          nama_acara: j.nama_acara || '',
          penyelenggara: j.penyelenggara || '',
          lokasi: j.lokasi || '',
          no_surat: j.no_surat || '',
          keterangan: j.keterangan || '',
        });
        setExistingFile(j.lampiran);
        setSelectDisposisi(j.disposisi == 1 ? true : false);
        setSelectedPimpinan(j.pimpinan?.map(p => p.id) || []);
      })
      .catch(() => toast.error('Gagal memuat data jadwal'))
      .finally(() => setLoadingEdit(false));
  }, [editId, isEdit]);

  const set = (field) => (e) => {
    setForm(f => ({ ...f, [field]: e.target.value }));
    if (errors[field]) setErrors(err => ({ ...err, [field]: '' }));
  };

  const togglePimpinan = (id) =>
    setSelectedPimpinan(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const toggleDisposisi = () =>
    setSelectDisposisi(!selectDisposisi);

  const validate = () => {
    const e = {};
    if (!form.tanggal) e.tanggal = 'Tanggal wajib diisi';
    if (!form.jam_mulai) e.jam_mulai = 'Jam mulai wajib diisi';
    if (!form.nama_acara.trim()) e.nama_acara = 'Nama acara wajib diisi';
    if (form.jam_selesai && form.jam_selesai <= form.jam_mulai)
      e.jam_selesai = 'Jam selesai harus setelah jam mulai';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append('pimpinan_ids', JSON.stringify(selectedPimpinan));
      fd.append('disposisi', selectDisposisi);
      if (file) fd.append('lampiran', file);

      if (isEdit) {
        await api.put(`/jadwal/${editId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Jadwal berhasil diperbarui!');
      } else {
        await api.post('/jadwal', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Jadwal berhasil disimpan!');
      }
      navigate('/jadwal');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Gagal menyimpan jadwal');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setForm(makeEmptyForm(defaultTanggal));
    setFile(null);
    setSelectedPimpinan([]);
    setErrors({});
    setSelectDisposisi(false);
  };

  if (loadingEdit) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
      <div className="loader" style={{ borderColor: 'var(--gray-200)', borderTopColor: 'var(--primary)', width: 32, height: 32, borderWidth: 3 }} />
    </div>
  );

  return (
    <div className="page-container" style={{ maxWidth: 760 }}>
      <button className="btn btn-ghost btn-sm" style={{ marginBottom: 20 }} onClick={() => navigate(-1)}>
        <ChevronLeft size={16} /> Kembali
      </button>

      <div style={{ background: 'linear-gradient(135deg, var(--secondary), var(--secondary-light))', borderRadius: 'var(--radius-lg)', padding: '28px 32px', color: 'white', marginBottom: 28 }}>
        <h1 style={{ fontSize: 26, fontWeight: 800 }}>{isEdit ? 'Edit Jadwal Acara' : 'Input Jadwal Acara'}</h1>
        <p style={{ opacity: 0.8, fontSize: 14, marginTop: 4 }}>
          {isEdit ? 'Perbarui data agenda kegiatan pimpinan' : 'Tambahkan agenda kegiatan pimpinan baru'}
        </p>
      </div>

      <div className="card">
        <div className="card-body">
          <form onSubmit={handleSubmit} noValidate style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

            {/* Tanggal & Jam */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
              <div className="form-group">
                <label className="form-label"><Calendar size={14} /> Tanggal <span style={{ color: 'var(--red)' }}>*</span></label>
                <input className={`form-input ${errors.tanggal ? 'input-error' : ''}`} type="date" value={form.tanggal} onChange={set('tanggal')} />
                {errors.tanggal && <span className="field-error">{errors.tanggal}</span>}
              </div>
              <div className="form-group">
                <label className="form-label"><Clock size={14} /> Jam Mulai <span style={{ color: 'var(--red)' }}>*</span></label>
                <input className={`form-input ${errors.jam_mulai ? 'input-error' : ''}`} type="time" value={form.jam_mulai} onChange={set('jam_mulai')} />
                {errors.jam_mulai && <span className="field-error">{errors.jam_mulai}</span>}
              </div>
              <div className="form-group">
                <label className="form-label"><Clock size={14} /> Jam Selesai</label>
                <input className={`form-input ${errors.jam_selesai ? 'input-error' : ''}`} type="time" value={form.jam_selesai} onChange={set('jam_selesai')} />
                {errors.jam_selesai && <span className="field-error">{errors.jam_selesai}</span>}
              </div>
            </div>

            {/* Nama Acara */}
            <div className="form-group">
              <label className="form-label"><FileText size={14} /> Nama Acara <span style={{ color: 'var(--red)' }}>*</span></label>
              <input className={`form-input ${errors.nama_acara ? 'input-error' : ''}`} type="text"
                placeholder="Masukkan nama acara" value={form.nama_acara} onChange={set('nama_acara')} />
              {errors.nama_acara && <span className="field-error">{errors.nama_acara}</span>}
            </div>

            {/* Pimpinan */}
            {pimpinanList.length > 0 && (
              <div className="form-group">
                <label className="form-label"><Users size={14} /> Pimpinan yang Hadir</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '12px', border: '1.5px solid var(--gray-200)', borderRadius: 8, background: 'var(--gray-50)' }}>
                  {pimpinanList.map(p => (
                    <label key={p.id} style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
                      background: selectedPimpinan.includes(p.id) ? 'var(--primary-bg)' : 'white',
                      border: `1.5px solid ${selectedPimpinan.includes(p.id) ? 'var(--primary)' : 'var(--gray-200)'}`,
                      borderRadius: 8, cursor: 'pointer', transition: 'all 0.15s', userSelect: 'none',
                    }}>
                      <input type="checkbox" style={{ display: 'none' }}
                        checked={selectedPimpinan.includes(p.id)} onChange={() => togglePimpinan(p.id)} />
                      <div style={{
                        width: 18, height: 18, borderRadius: 4, flexShrink: 0,
                        background: selectedPimpinan.includes(p.id) ? 'var(--primary)' : 'white',
                        border: `2px solid ${selectedPimpinan.includes(p.id) ? 'var(--primary)' : 'var(--gray-300)'}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {selectedPimpinan.includes(p.id) && <span style={{ color: 'white', fontSize: 12, fontWeight: 700 }}>✓</span>}
                      </div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: selectedPimpinan.includes(p.id) ? 'var(--primary)' : 'var(--gray-700)' }}>{p.nama}</div>
                        <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>{p.jabatan}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Penyelenggara & Lokasi */}
            <div className="grid-2">
              <div className="form-group">
                <label className="form-label"><Building2 size={14} /> Penyelenggara</label>
                <input className="form-input" type="text" placeholder="Nama penyelenggara/pengundang" value={form.penyelenggara} onChange={set('penyelenggara')} />
              </div>
              <div className="form-group">
                <label className="form-label"><MapPin size={14} /> Lokasi Acara</label>
                <input className="form-input" type="text" placeholder="Tempat pelaksanaan" value={form.lokasi} onChange={set('lokasi')} />
              </div>
            </div>

            {/* No Surat & Lampiran */}
            <div className="grid-2">
              <div className="form-group">
                <label className="form-label"><FileText size={14} /> No Surat</label>
                <input className="form-input" type="text" placeholder="Nomor surat undangan" value={form.no_surat} onChange={set('no_surat')} />
              </div>
              <div className="form-group">
                <label className="form-label"><Paperclip size={14} /> Lampiran</label>
                <label className="upload-area" style={{ display: 'block', cursor: 'pointer' }}>
                  <input type="file" style={{ display: 'none' }} accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    onChange={e => setFile(e.target.files[0])} />
                  <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>
                    {file ? <span style={{ color: 'var(--primary)', fontWeight: 600 }}>📎 {file.name}</span>
                      : existingFile ? <span style={{ color: 'var(--secondary)' }}>📎 {existingFile} (tersimpan)</span>
                        : <span>⬆️ Upload File (PDF, DOC, JPG, PNG, maks 5MB)</span>}
                  </div>
                </label>
              </div>
            </div>
            {/* disposisi */}
            <label style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
              background: selectDisposisi ? 'var(--primary-bg)' : 'white',
              border: `1.5px solid ${selectDisposisi ? 'var(--primary)' : 'var(--gray-200)'}`,
              borderRadius: 8, cursor: 'pointer', transition: 'all 0.15s', userSelect: 'none',
            }}>
              <input type="checkbox" style={{ display: 'none' }}
                checked={selectDisposisi} onChange={() => toggleDisposisi()} />
              <div style={{
                width: 18, height: 18, borderRadius: 4, flexShrink: 0,
                background: selectDisposisi ? 'var(--primary)' : 'white',
                border: `2px solid ${selectDisposisi ? 'var(--primary)' : 'var(--gray-300)'}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {selectDisposisi && <span style={{ color: 'white', fontSize: 12, fontWeight: 700 }}>✓</span>}
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: selectDisposisi ? 'var(--primary)' : 'var(--gray-700)' }}>Diposisi Acara</div>
              </div>
            </label>
            {/* Keterangan */}
            <div className="form-group">
              <label className="form-label"><AlignLeft size={14} /> Keterangan</label>
              <textarea className="form-input" placeholder="Keterangan tambahan (opsional)"
                value={form.keterangan} onChange={set('keterangan')} rows={3} />
            </div>

            <div style={{ display: 'flex', gap: 12, paddingTop: 4 }}>
              <button type="submit" className="btn btn-secondary btn-lg" disabled={loading} style={{ flex: 1 }}>
                {loading ? <span className="loader" /> : <Check size={18} />}
                {loading ? 'Menyimpan...' : (isEdit ? 'Perbarui Jadwal' : 'Simpan Jadwal')}
              </button>
              {!isEdit && (
                <button type="button" className="btn btn-outline btn-lg" onClick={handleReset}>
                  <RotateCcw size={18} /> Reset
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
